import * as React from 'react';
import { Alert, Button, Card, Form, Modal, Spinner, Stack } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { PageTitle, selectLoginStateWaiting, selectUser } from 'gossamer-front-end';
import { SecurityContext } from 'gossamer-unified';
import { CustomInfo } from 'cb-unified';
import { PlanOptions } from '../website-auth/PlanOptions';
import {
  BillingInterval,
  createUpgradeCheckoutSession,
  getSubscriptionCheckoutSessionStatus,
  PlanOptionId,
  sendContactMessage,
  upgradeFromFree,
} from '../website-auth/api';

const UPGRADE_PATH = '/website/upgrade-plan';

type PaidPlanId = 'pro' | 'premium';

const isPaidPlanId = (value: string | null): value is PaidPlanId => value === 'pro' || value === 'premium';

// institution types include legacy values ('free', 'paid', 'marketing') alongside the current plan ids
const toPlanOptionId = (institutionType: string | null | undefined): PlanOptionId | undefined => {
  switch (institutionType) {
    case 'personal':
    case 'free':
      return 'personal';
    case 'paid':
    case 'pro':
      return 'pro';
    case 'marketing':
    case 'premium':
      return 'premium';
    case 'enterprise':
      return 'enterprise';
    default:
      return undefined;
  }
};

const splitName = (name: string | undefined): { firstName: string; lastName: string } => {
  const parts = (name ?? '').trim().split(' ').filter(Boolean);
  return { firstName: parts[0] || 'Upgrade', lastName: parts.slice(1).join(' ') || 'Enquiry' };
};

const getErrorMessage = (e: unknown, fallback: string): string => (e instanceof Error ? e.message : fallback);

const sendPlanMessage = async (
  user: SecurityContext,
  email: string,
  subject: string,
  details: (string | null)[],
  body: string,
): Promise<void> =>
  sendContactMessage({
    ...splitName(user.name),
    email,
    message: [subject, '', `Name: ${user.name}`, `UserId: ${user.userId}`, `Email: ${email}`, ...details, '', body]
      .filter((line): line is string => line !== null)
      .join('\n'),
  });

const EnquiryModal = (props: {
  user: SecurityContext;
  currentPlanId?: PlanOptionId;
  requestedPlanId?: PlanOptionId;
  onHide: () => void;
}): React.JSX.Element => {
  const [email, setEmail] = React.useState(props.user.email ?? '');
  const [enquiry, setEnquiry] = React.useState('');
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!enquiry.trim()) {
      setError('Please enter a short description of your enquiry.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await sendPlanMessage(
        props.user,
        email.trim(),
        'Plan change enquiry (Upgrade page)',
        [
          props.currentPlanId ? `Current plan: ${props.currentPlanId}` : null,
          props.requestedPlanId ? `Requested plan: ${props.requestedPlanId}` : null,
        ],
        `Enquiry:\n${enquiry.trim()}`,
      );
      setIsSubmitted(true);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to submit enquiry. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show onHide={props.onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Plan enquiry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isSubmitted ? (
          <Alert variant="success" className="mb-0">
            Thank you - we will get back to you shortly.
          </Alert>
        ) : (
          <Stack gap={3}>
            {error && <Alert variant="danger">{error}</Alert>}
            <p className="mb-0">Tell us what you need and we will get back to you shortly.</p>
            <Form.Group controlId="upgrade-enquiry-email">
              <Form.Label>Email address</Form.Label>
              <Form.Control type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </Form.Group>
            <Form.Group controlId="upgrade-enquiry-message">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={enquiry}
                onChange={(event) => setEnquiry(event.target.value)}
              />
            </Form.Group>
          </Stack>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isSubmitted ? (
          <Button onClick={props.onHide}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={props.onHide} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={isSubmitting}>
              {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
              Send enquiry
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// for admins of institutions on legacy plans, which can only be changed by the support team
const SupportRequest = (props: {
  user: SecurityContext;
  adminInstitutionId: number;
  adminInstitutionType: string;
}): React.JSX.Element => {
  const [request, setRequest] = React.useState('');
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!request.trim()) {
      setError('Please enter your request.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await sendPlanMessage(
        props.user,
        props.user.email,
        'Plan upgrade request (Upgrade page - legacy institution plan)',
        [`Admin institution id: ${props.adminInstitutionId}`, `Admin institution type: ${props.adminInstitutionType}`],
        `Request:\n${request.trim()}`,
      );
      setIsSubmitted(true);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to send. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card style={{ maxWidth: 640 }}>
      <Card.Body>
        <Card.Title as="h2" className="h4">
          Contact support
        </Card.Title>
        {isSubmitted ? (
          <Alert variant="success" className="mb-0">
            Sent - thank you. We will get back to you shortly.
          </Alert>
        ) : (
          <Form onSubmit={submit}>
            <Stack gap={3}>
              <p className="mb-0">
                To change plans for your institution, please send a request to support and we will get back to you.
              </p>
              {error && <Alert variant="danger">{error}</Alert>}
              <Form.Group controlId="upgrade-support-request">
                <Form.Label>Your request</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={request}
                  disabled={isSubmitting}
                  onChange={(event) => setRequest(event.target.value)}
                />
              </Form.Group>
              <div>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Spinner className="me-2" animation="border" size="sm" />}
                  Send
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

const InstitutionNameStep = (props: {
  user: SecurityContext;
  planType: PaidPlanId;
  billingInterval: BillingInterval;
  onCancel: () => void;
}): React.JSX.Element => {
  const [institutionName, setInstitutionName] = React.useState('');
  const [error, setError] = React.useState('');
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const beginCheckout = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!institutionName.trim()) {
      setError('Please enter an institution name.');
      return;
    }
    setIsRedirecting(true);
    setError('');
    try {
      const params = new URLSearchParams({ plan: props.planType, institutionName: institutionName.trim() });
      const checkout = await createUpgradeCheckoutSession({
        planType: props.planType,
        ...splitName(props.user.name),
        email: props.user.email,
        institutionName: institutionName.trim(),
        billingInterval: props.billingInterval,
        returnPath: `${UPGRADE_PATH}?${params.toString()}`,
      });
      if (!checkout.url) throw new Error('The subscription checkout URL was not returned.');
      window.location.assign(checkout.url);
    } catch (e) {
      setError(getErrorMessage(e, 'Could not start checkout.'));
      setIsRedirecting(false);
    }
  };

  return (
    <Form onSubmit={beginCheckout} style={{ maxWidth: 640 }}>
      <Stack gap={3}>
        <h2 className="h4 mb-0">Upgrade to {props.planType === 'pro' ? 'Pro' : 'Premium'}</h2>
        <p className="mb-0">
          For the Pro or Premium plans, you need to provide a name for the society, organisation or institution you
          represent. You will then be redirected to Stripe to complete payment, and return here once it is complete.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="upgrade-institution-name">
          <Form.Label>Institution name</Form.Label>
          <Form.Control
            value={institutionName}
            placeholder="Your institution / company name"
            disabled={isRedirecting}
            onChange={(event) => setInstitutionName(event.target.value)}
          />
        </Form.Group>
        <Stack direction="horizontal" gap={2}>
          <Button type="button" variant="outline-secondary" disabled={isRedirecting} onClick={props.onCancel}>
            Back
          </Button>
          <Button type="submit" disabled={isRedirecting}>
            {isRedirecting && <Spinner className="me-2" animation="border" size="sm" />}
            Continue to payment
          </Button>
        </Stack>
      </Stack>
    </Form>
  );
};

// on return from Stripe: verify the checkout session, then convert the user's account to the paid plan
const FinaliseStep = (props: { planType: PaidPlanId; institutionName: string; sessionId: string }) => {
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const status = await getSubscriptionCheckoutSessionStatus(props.sessionId);
      const complete = status.checkoutStatus === 'complete';
      const paid = status.paymentStatus === 'paid' || status.paymentStatus === 'no_payment_required';
      if (!complete || !paid) throw new Error(status.error || 'Subscription was not completed.');
      await upgradeFromFree({
        planType: props.planType,
        institutionName: props.institutionName,
        subscriptionCheckoutSessionId: props.sessionId,
      });
      // full reload, so that the refreshed session (with the new institution) is picked up
      window.location.assign('/admin');
    })().catch((e) => {
      if (!cancelled) setError(getErrorMessage(e, 'Could not complete your upgrade.'));
    });
    return () => {
      cancelled = true;
    };
  }, [props.planType, props.institutionName, props.sessionId]);

  return error ? (
    <Alert variant="danger">
      {error} Please try again or <a href="/website/contact-us">contact support</a>.
    </Alert>
  ) : (
    <div className="text-center py-4">
      <Spinner animation="border" />
      <p className="mt-3 mb-0">
        Finalising your upgrade... <strong>Please do not close your browser or navigate away from this page.</strong>
      </p>
    </div>
  );
};

const UpgradePlan = (props: { user: SecurityContext }): React.JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customInfo = props.user.customInfo as CustomInfo | undefined;
  const adminInstitutionId = customInfo?.adminInstitutionId;
  const adminInstitutionType = customInfo?.adminInstitutionType?.toLowerCase() ?? '';
  const currentPlanId = toPlanOptionId(adminInstitutionType || customInfo?.createEventInstitutionType);
  // only users without an institution can upgrade themselves - other plan changes go via the sales team
  const canSelfUpgrade = props.user.roleId === 'user' && currentPlanId === 'personal';

  const [billingInterval, setBillingInterval] = React.useState<BillingInterval>('monthly');
  const [selectedPlanId, setSelectedPlanId] = React.useState<PaidPlanId>();
  const [enquiryPlanId, setEnquiryPlanId] = React.useState<PlanOptionId>();

  const plan = searchParams.get('plan');
  const institutionName = searchParams.get('institutionName');
  const subscriptionResult = searchParams.get('subscription');
  const sessionId = searchParams.get('session_id');

  if (subscriptionResult === 'success' && sessionId && isPaidPlanId(plan) && institutionName) {
    return <FinaliseStep planType={plan} institutionName={institutionName} sessionId={sessionId} />;
  }

  if (
    typeof adminInstitutionId === 'number' &&
    (adminInstitutionType === 'paid' || adminInstitutionType === 'society')
  ) {
    return (
      <SupportRequest
        user={props.user}
        adminInstitutionId={adminInstitutionId}
        adminInstitutionType={adminInstitutionType}
      />
    );
  }

  if (selectedPlanId) {
    return (
      <InstitutionNameStep
        user={props.user}
        planType={selectedPlanId}
        billingInterval={billingInterval}
        onCancel={() => setSelectedPlanId(undefined)}
      />
    );
  }

  const selectPlan = (planOptionId: PlanOptionId) => {
    if (canSelfUpgrade && isPaidPlanId(planOptionId)) {
      // clear any query string left by a cancelled checkout
      navigate(UPGRADE_PATH, { replace: true });
      setSelectedPlanId(planOptionId);
    } else {
      setEnquiryPlanId(planOptionId);
    }
  };

  return (
    <>
      {subscriptionResult === 'cancel' && (
        <Alert variant="warning">Payment was cancelled, so your plan has not changed. You can try again below.</Alert>
      )}
      <PlanOptions
        billingInterval={billingInterval}
        onBillingIntervalChange={setBillingInterval}
        onSelect={selectPlan}
        currentPlanId={currentPlanId}
        disabledPlanIds={['personal']}
        ctaLabels={canSelfUpgrade ? undefined : { pro: 'Contact sales', premium: 'Contact sales' }}
      />
      {enquiryPlanId && (
        <EnquiryModal
          user={props.user}
          currentPlanId={currentPlanId}
          requestedPlanId={enquiryPlanId}
          onHide={() => setEnquiryPlanId(undefined)}
        />
      )}
    </>
  );
};

export const WebsiteUpgradePlanPage = (): React.JSX.Element => {
  const initialising = useSelector(selectLoginStateWaiting);
  const user = useSelector(selectUser);
  const [searchParams] = useSearchParams();

  if (initialising) {
    return <div>Restoring user session...</div>;
  }
  if (!user) {
    // keep the query string, so that a return from Stripe can still be finalised after logging in again
    const query = searchParams.toString();
    const redirectToPath = query ? `${UPGRADE_PATH}?${query}` : UPGRADE_PATH;
    return <Navigate to={`/auth/login?redirectToPath=${encodeURIComponent(redirectToPath)}`} replace />;
  }
  return (
    <>
      <PageTitle title="Upgrade your plan" />
      <UpgradePlan user={user} />
    </>
  );
};
